/**
 * What Coach offers at every place (plan N, S11): the one table the voice
 * and the chat both read, so the line under the orb and the chat's first line
 * can never say unrelated things. The phone keeps no opening table of its
 * own. The day's spoken greeting offers nothing: it greets, then asks how it
 * can help (Devendra, 26 Sep 2026).
 *
 * It carries no fact and no number (FD2): each place offers what the person
 * can do there, and nothing about what the business holds. The words are keys
 * in the catalogue (`agent.opening.*`, `agent.greet.*` and `agent.suggest.*`),
 * and none of them names Coach or promises to look something up (FD7).
 */
import type { TimeOfDay } from './format';
import type { AppKey } from './i18n/en';
import { t } from './i18n/t';

/**
 * Every place the assistant opens at. `settings` is an Account page pushed
 * over the tab; `business` is the business's own detail page, and `knows` is
 * What Coach knows.
 */
export const AGENT_PLACES = [
  'default',
  'home',
  'dashboard',
  'record',
  'new_entry',
  'entry',
  'customize',
  'links',
  'insights',
  // One insight, opened on its own page (web-redesign-10 phase 5): OS beside it, asked about it.
  'insight',
  'business',
  'team',
  'knows',
  'account',
  'settings',
  'roles',
  // The web's Report templates pages (D24 of docs/plans/report-templates.md): a record's templates, and one template open.
  'documents',
  'document',
] as const;

export type AgentPlace = (typeof AGENT_PLACES)[number];

/** The place a screen name is: the name itself when it is a place, otherwise `default`. */
export const agentPlaceOf = (screen: string): AgentPlace =>
  (AGENT_PLACES as readonly string[]).includes(screen) ? (screen as AgentPlace) : 'default';

/** One place's opening, as keys. */
export type AgentOpening = {
  readonly place: AgentPlace;
  /**
   * The one line of actions. It is the chat's first line (S9), and the muted
   * line under the orb on a later voice-first open (S10, C12).
   */
  readonly offer: AppKey;
  /** Three to five chips to try, the primary first. */
  readonly chips: readonly AppKey[];
  /** The first thing to try: always `chips[0]`. */
  readonly primary: AppKey;
  /** Whether the words fill `{{record}}` with the record's name. */
  readonly named: boolean;
};

type OpeningRow = { readonly offer: AppKey; readonly chips: readonly AppKey[]; readonly named: boolean };

/** The catalogue: every place's offer and chips, the primary first. */
export const OPENINGS: Readonly<Record<AgentPlace, OpeningRow>> = {
  default: {
    offer: 'agent.opening.default',
    chips: ['agent.suggest.default.1', 'agent.suggest.default.2', 'agent.suggest.home.3'],
    named: false,
  },
  // The Records tab draws the default line (C18).
  home: {
    offer: 'agent.opening.default',
    chips: ['agent.suggest.home.4', 'agent.suggest.home.1', 'agent.suggest.home.2', 'agent.suggest.home.3'],
    named: false,
  },
  dashboard: {
    offer: 'agent.opening.dashboard',
    chips: ['agent.suggest.dashboard.1', 'agent.suggest.dashboard.2', 'agent.suggest.dashboard.3'],
    named: false,
  },
  record: {
    offer: 'agent.opening.record',
    chips: ['agent.suggest.record.1', 'agent.suggest.record.2', 'agent.suggest.record.3', 'agent.suggest.record.4', 'agent.suggest.record.5'],
    named: true,
  },
  new_entry: {
    offer: 'agent.opening.new_entry',
    chips: ['agent.suggest.new_entry.3', 'agent.suggest.new_entry.1', 'agent.suggest.new_entry.2'],
    named: true,
  },
  entry: {
    offer: 'agent.opening.entry',
    chips: ['agent.suggest.entry.1', 'agent.suggest.new_entry.1', 'agent.suggest.new_entry.2'],
    named: true,
  },
  customize: {
    offer: 'agent.opening.customize',
    chips: ['agent.suggest.customize.1', 'agent.suggest.customize.2', 'agent.suggest.customize.3'],
    named: true,
  },
  links: {
    offer: 'agent.opening.links',
    chips: ['agent.suggest.links.3', 'agent.suggest.links.1', 'agent.suggest.links.2'],
    named: true,
  },
  insights: {
    offer: 'agent.opening.insights',
    chips: ['agent.suggest.record.4', 'agent.suggest.insights.1', 'agent.suggest.insights.2'],
    named: true,
  },
  // An insight opened on its own page: questions about what it shows. The web draws its own line with the insight's name.
  insight: {
    offer: 'agent.opening.insight',
    chips: ['agent.suggest.insight.1', 'agent.suggest.insight.2', 'agent.suggest.insight.3'],
    named: false,
  },
  business: {
    offer: 'agent.opening.business',
    chips: ['agent.suggest.team.1', 'agent.suggest.business.3', 'agent.suggest.business.2'],
    named: false,
  },
  team: {
    offer: 'agent.opening.team',
    chips: ['agent.suggest.team.1', 'agent.suggest.team.2', 'agent.suggest.business.3'],
    named: false,
  },
  knows: {
    offer: 'agent.opening.knows',
    chips: ['agent.suggest.knows.1', 'agent.suggest.knows.2', 'agent.suggest.business.2'],
    named: false,
  },
  account: {
    offer: 'agent.opening.account',
    chips: ['agent.suggest.account.1', 'agent.suggest.account.2', 'agent.suggest.home.3'],
    named: false,
  },
  // An Account page pushed over the tab: the account row's words, in the same order.
  settings: {
    offer: 'agent.opening.account',
    chips: ['agent.suggest.account.1', 'agent.suggest.account.2', 'agent.suggest.home.3'],
    named: false,
  },
  // The web's Roles page: what a role lets people do, and who holds it.
  roles: {
    offer: 'agent.opening.roles',
    chips: ['agent.suggest.roles.1', 'agent.suggest.roles.2', 'agent.suggest.roles.3'],
    named: false,
  },
  // The web's Report templates tab of a record: which document to make from it.
  documents: {
    offer: 'agent.opening.documents',
    chips: ['agent.suggest.documents.1', 'agent.suggest.documents.2', 'agent.suggest.documents.3'],
    named: true,
  },
  // One template open in the designer: what to change on it.
  document: {
    offer: 'agent.opening.document',
    chips: ['agent.suggest.document.1', 'agent.suggest.document.2', 'agent.suggest.document.3'],
    named: false,
  },
};

/**
 * A place's opening. A place whose words name the record, with no record to
 * name, falls back to the whole `default` entry, and `place` is then
 * `default`.
 */
export const openingOf = (screen: string, hasRecord: boolean): AgentOpening => {
  const asked = agentPlaceOf(screen);
  const place: AgentPlace = OPENINGS[asked].named && !hasRecord ? 'default' : asked;
  const row = OPENINGS[place];
  const [primary] = row.chips;
  if (primary === undefined) throw new Error(`The opening of ${place} has no chips.`);
  return { place, offer: row.offer, chips: row.chips, primary, named: row.named };
};

/**
 * The one wording of an opening: the offer and every chip, resolved, with the
 * record's name where the words want one. The chat's first line (4.2), the
 * voice look's offer line (Phase 6) and chat first (Phase 13) all read it, so
 * the line under the orb and the chat's first line can never differ.
 */
export const openingWordsOf = (
  screen: string,
  recordName: string | null,
): { readonly place: AgentPlace; readonly offer: string; readonly chips: readonly string[] } => {
  const opening = openingOf(screen, recordName !== null);
  const options = recordName === null ? undefined : { record: recordName };
  return { place: opening.place, offer: t(opening.offer, options), chips: opening.chips.map((chip) => t(chip, options)) };
};

/** The day's first greeting, by the part of the day, with the first name or without one (FD4). */
export const greetKeyOf = (time: TimeOfDay, named: boolean): AppKey =>
  named ? `agent.greet.${time}` : `agent.greet.${time}.no_name`;

/**
 * The first name a greeting says (S12): the first whitespace-separated word of
 * the trimmed name, never re-cased, and nothing for no name.
 */
export const firstNameOf = (name: string | null | undefined): string => {
  if (name === null || name === undefined) return '';
  return name.trim().split(/\s+/)[0] ?? '';
};
